import { Box, Typography, Paper } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';

import Input from 'components/Common/Input';
import DualInput from 'components/Common/DualInput';
import TelInput from 'components/Common/TelInput';
import Select from 'components/Common/Select';
import Button from 'components/Common/Button';
import GoBackButtonIcon from '@/assets/icons/GoBackButtonIcon';

import { useCreateOrUpdateTeamMember, useGetTeamMember } from 'apis/business/setting/team/hook';
import { useGetLocations } from '@/apis/business/setting/location/hook';
import { teamMemberSchema } from 'utils/schema/validator/TeamMemberValidation';
import TeamMemberFormSkeleton from './TeamMemberFormSkeleton';
import WorkingHours from '../../shared/workingHours';

const defaultWorkingHours = {
  monday: { open: false, from_time: '', to_time: '', breaks: [] },
  tuesday: { open: false, from_time: '', to_time: '', breaks: [] },
  wednesday: { open: false, from_time: '', to_time: '', breaks: [] },
  thursday: { open: false, from_time: '', to_time: '', breaks: [] },
  friday: { open: false, from_time: '', to_time: '', breaks: [] },
  saturday: { open: false, from_time: '', to_time: '', breaks: [] },
  sunday: { open: false, from_time: '', to_time: '', breaks: [] }
};

const toLocationIds = (value: unknown): number[] => {
  if (Array.isArray(value)) {
    return value.map(Number).filter((id) => Number.isFinite(id) && id > 0);
  }
  if (value === '' || value == null) return [];
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? [id] : [];
};

export default function TeamMemberForm() {
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();

  const { data: teamMember, isLoading } = useGetTeamMember({ include: 'locations' });
  const { data: LocationsData } = useGetLocations({ per_page: 9999 });
  const { mutate: handleTeamMember, isPending } = useCreateOrUpdateTeamMember();

  const defaultLocationIds =
    teamMember?.locations?.map((loc) => loc.id) || toLocationIds(teamMember?.location_ids) || [];

  const { handleSubmit, control, watch, getValues, setValue } = useForm<any>({
    resolver: zodResolver(teamMemberSchema),
    defaultValues: {
      team_member: {
        working_hours: defaultWorkingHours,
        holidays: [],
        sms_consent: false,
        location_ids: []
      }
    },
    values: teamId
      ? {
          team_member: {
            ...teamMember,
            first_name: teamMember?.first_name || teamMember?.name || '',
            location_ids: defaultLocationIds,
            working_hours: teamMember?.working_hours || defaultWorkingHours,
            holidays: teamMember?.holidays || [],
            sms_consent: teamMember?.sms_consent ?? false
          }
        }
      : undefined
  });

  const email = watch('team_member.email');
  const phone = watch('team_member.phone_number');
  const extension = watch('team_member.extension');
  const sipUri = watch('team_member.sip_uri');
  const role = watch('team_member.role');
  const isLocationAdmin = role === 'location_admin';
  const isOperator = role === 'operator';

  useEffect(() => {
    if (!isLocationAdmin) {
      setValue('team_member.location_ids', []);
    }
  }, [isLocationAdmin, setValue]);

  const hasEmail = Boolean((email ?? '').trim());
  const hasPhone = Boolean(String(phone ?? '').replace(/[^\d]/g, '').length > 4);
  const hasExtension = Boolean(String(extension ?? '').trim());
  const hasSip = Boolean(String(sipUri ?? '').trim());
  const hasContactMethod = hasPhone || hasExtension || hasSip;

  // Operator: email required only when no phone/extension/SIP. Platform roles: email always required.
  const isEmailRequired = isOperator ? !hasContactMethod : Boolean(role);
  // Operator: phone optional if extension/SIP/email present. Platform roles: phone optional.
  const isPhoneRequired = isOperator && !hasExtension && !hasSip && !hasEmail;

  const locationOptions = (LocationsData?.data ?? []).map((location) => ({
    label: location.name,
    value: location.id
  }));

  const onSubmit = (data: any) => {
    if (data.team_member.role !== 'location_admin') {
      delete data.team_member.location_ids;
    } else {
      data.team_member.location_ids = toLocationIds(data.team_member.location_ids);
    }
    handleTeamMember(data);
  };

  if (isLoading) return <TeamMemberFormSkeleton />;

  const phoneField = (
    <Grid size={{ xs: 12, md: 6 }}>
      <TelInput
        name='team_member.phone_number'
        label='Phone Number'
        control={control}
        defaultCountry='US'
        fullWidth
        consent_key='team_member.sms_consent'
        required={isPhoneRequired}
      />
      <Typography variant='caption' color='text.secondary' display='block' mt={0.75}>
        Use a different number than the one assigned to your AI Agent.
      </Typography>
    </Grid>
  );

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)}>
      <Box display='flex' alignItems='flex-start' mb={3} gap={0.8}>
        <Box
          onClick={() => navigate(-1)}
          display='flex'
          alignItems='center'
          justifyContent='center'
          mt={0.5}
          sx={{ cursor: 'pointer' }}
        >
          <GoBackButtonIcon />
        </Box>
        <Box>
          <Typography variant='h6' fontWeight={500}>
            {teamId ? 'Edit Member' : 'Add Member'}
          </Typography>
          <Typography variant='body2' color='text.secondary' mt={0.5}>
            Add human agents who can access platform take over or assist on live conversations.
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          p: 3,
          border: '1px solid #ECECEC'
        }}
      >
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Input
              name='team_member.first_name'
              label='Name'
              control={control}
              placeholder='Enter Name'
              fullWidth
              required
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Input
              name='team_member.email'
              label='Email'
              control={control}
              placeholder='contact@business.com'
              fullWidth
              required={isEmailRequired}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Select
              name='team_member.role'
              label='Role'
              control={control}
              fullWidth
              required
              options={[
                { label: 'Business Admin', value: 'business_admin' },
                { label: 'Location Admin', value: 'location_admin' },
                { label: 'Operator (No Platform Access)', value: 'operator' }
              ]}
            />
            <Typography variant='caption' color='text.secondary' display='block' mt={0.75}>
              Select a role for this member. It defines their access and permissions within the business and does not
              apply to the AI Agent.
            </Typography>
          </Grid>

          {isLocationAdmin ? (
            <Grid size={{ xs: 12, md: 6 }}>
              <Select
                name='team_member.location_ids'
                label='Location'
                control={control}
                fullWidth
                required
                multiple
                displayEmpty
                sortOrder='none'
                visibleCount={3}
                options={locationOptions}
              />
            </Grid>
          ) : (
            phoneField
          )}

          {isLocationAdmin && phoneField}

          <Grid size={{ xs: 12, md: 6 }}>
            <DualInput
              name={['team_member.main_line_number', 'team_member.extension']}
              type={['text', 'number']}
              placeholder={['Enter Main Line Number', 'Enter Extension Here']}
              label='Main Line Number/Extension'
              control={control}
              fullWidth
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Input
              name='team_member.sip_uri'
              label='SIP URI'
              control={control}
              placeholder='sip:user@domain.com'
              fullWidth
            />
          </Grid>
        </Grid>

        <Box mt={4}>
          <WorkingHours
            getValues={getValues}
            setValue={setValue}
            control={control}
            fieldName='team_member.working_hours'
            holidaysFieldName='team_member.holidays'
          />
        </Box>
      </Paper>

      <Box display='flex' justifyContent='end' gap={1} mt={3}>
        <Button
          color='secondary'
          variant='outlined'
          sx={{ border: '1px solid #F3F3F3', background: '#fff' }}
          onClick={() => navigate(-1)}
        >
          Cancel
        </Button>
        <Button type='submit' variant='contained' isPending={isPending}>
          {teamId ? 'Save Member' : 'Add Member'}
        </Button>
      </Box>
    </Box>
  );
}
